import React, { useState } from "react";
import Modal from "../UI/Modal";
import Overlay from "../UI/Overlay";
import Header2 from "../UI/textFields/Header2";
import Body2 from "../UI/textFields/Body2";
import StandardButton from "../UI/buttons/StandardButton";
import { TaskRefContext } from "../../types/apiTypes";
import labels from "../../utils/labels";
import { useAppDispatch } from "../../store/hooks";
import { closeNexusErrorModal } from "../../store/overlay";
import { ReactComponent as WarningIcon } from "../../assets/icons/warning.svg";

const MAX_TASKS_INITIAL = 4; // Show first 4 tasks initially (matching the design)

interface NexusExportErrorModalProps {
  isOpen: boolean;
  failedTasks?: TaskRefContext[];
  onForceSend?: () => void;
  message?: string;
}

const NexusExportErrorModal: React.FC<NexusExportErrorModalProps> = ({
  isOpen,
  failedTasks,
  onForceSend,
  message,
}) => {
  const dispatch = useAppDispatch();
  const [showAllTasks, setShowAllTasks] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    dispatch(closeNexusErrorModal());
  };

  const formatTaskDisplay = (task: TaskRefContext): string => {
    // Format: "Service Client Name - Description"
    // Example: "Mikkel Holm - Hjemmepleje besøg"

    let display = "";

    // Add description (task details)
    if (task.description) {
      display += `${task.description}`;
    }

    // Add address if available
    if (task.address) {
      display += ` (${task.address})`;
    }

    return display;
  };

  const handleExpandTasks = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowAllTasks(true);
  };

  const displayedTasks = !failedTasks
    ? []
    : showAllTasks
      ? failedTasks
      : failedTasks.slice(0, MAX_TASKS_INITIAL);

  const remainingTasksCount =
    failedTasks && failedTasks.length - MAX_TASKS_INITIAL;
  const hasMoreTasks =
    remainingTasksCount && remainingTasksCount > 0 && !showAllTasks;

  return (
    <Overlay darkBackground clickAway={handleClose} zIndex={1001}>
      <Modal>
        <div className="flex flex-col gap-[var(--spacing-md)] max-w-[500px]">
          <Header2>{labels.nexusExportErrorHeader}</Header2>

          <Body2>{labels.nexusExportErrorUnappliedTaskRefs}</Body2>

          {failedTasks && failedTasks.length > 0 && (
            <div className="flex flex-col gap-[var(--spacing-xs)] max-h-[300px] overflow-y-auto">
              {displayedTasks.map((task: TaskRefContext, index: number) => (
                <div
                  key={`${task.taskRef}-${index}`}
                  className="flex items-center gap-[var(--spacing-sm)]"
                >
                  <div className="flex-shrink-0">
                    <WarningIcon height={16} width={16} />
                  </div>
                  <span className="text-xs text-gray-900">
                    {formatTaskDisplay(task)}
                  </span>
                </div>
              ))}

              {hasMoreTasks && (
                <button
                  onClick={handleExpandTasks}
                  className="text-xs text-blue-600 hover:text-blue-800 text-left flex items-center gap-[var(--spacing-sm)]"
                >
                  <div className="flex-shrink-0 w-4"> </div>
                  <span>+{remainingTasksCount}</span>
                </button>
              )}
            </div>
          )}

          <div className="flex justify-end gap-[var(--spacing-sm)] w-full pt-[var(--spacing-sm)]">
            <StandardButton
              ariaLabel={labels.nexusExportErrorClose}
              variant="SECONDARY"
              onClick={handleClose}
            >
              {labels.nexusExportErrorClose}
            </StandardButton>

            {onForceSend && (
              <StandardButton
                ariaLabel={labels.nexusExportErrorRetry}
                variant="PRIMARY"
                onClick={() => {
                  handleClose();
                  onForceSend();
                }}
              >
                {labels.nexusExportErrorRetry}
              </StandardButton>
            )}
          </div>
        </div>
      </Modal>
    </Overlay>
  );
};

export default NexusExportErrorModal;
